"use client"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { CalendarIcon, ChevronDown } from "lucide-react"
import { format } from "date-fns"
import React from "react"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Session } from "@/lib/api"

interface GameInfoSectionProps {
  selectedGame: string
  onGameChange: (game: string) => void
  onGameTimeChange?: (time: string) => void
  activeSession?: Session | null
  sessions?: Session[]
  selectedSession?: string
  onSessionChange?: (sessionId: string) => void
  showSessionFilter?: boolean
}

interface CustomDropdownProps {
  trigger: React.ReactNode
  children: React.ReactNode
  className?: string
}

function CustomDropdown({ trigger, children, className }: CustomDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Clone children and give them access to setIsOpen
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, { closeDropdown: () => setIsOpen(false) })
    }
    return child
  })

  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <div
          className={cn(
            "absolute top-full left-0 mt-1 bg-red-800 text-white rounded-md shadow-lg z-50 min-w-[200px]",
            className,
          )}
        >
          {enhancedChildren}
        </div>
      )}
    </div>
  )
}

interface DropdownItemProps {
  onClick: () => void
  children: React.ReactNode
  closeDropdown?: () => void
}

function DropdownItem({ onClick, children, closeDropdown }: DropdownItemProps) {
  return (
    <div
      className="px-4 py-2 hover:bg-red-700 cursor-pointer text-white first:rounded-t-md last:rounded-b-md"
      onClick={() => {
        onClick()
        if (closeDropdown) closeDropdown()
      }}
    >
      {children}
    </div>
  )
}

export function GameInfoSection({ 
  selectedGame, 
  onGameChange, 
  onGameTimeChange,
  activeSession,
  sessions = [],
  selectedSession,
  onSessionChange,
  showSessionFilter = false
}: GameInfoSectionProps) {
  const [date, setDate] = React.useState<Date | undefined>(new Date())
  const [selectedGameTime, setSelectedGameTime] = React.useState("Morning (13:00)")
  const [countdown, setCountdown] = React.useState("00:00:00")

  const gameTimes = ["Morning (13:00)", "Day (18:00)", "Evening (20:00)"]

  // Auto-select game based on URL
  React.useEffect(() => {
    const path = window.location.pathname

    if (path.includes("/DearLottery/single/add-single") || path.includes("/single")) {
      onGameChange("Dear Single")
    } else if (path.includes("/DearLottery/rajshree/add-rajshree") || path.includes("/rajshree")) {
      onGameChange("Rajshree Punjab")
    } else if (path.includes("/DearLottery/choice/add-choice") || path.includes("/choice")) {
      onGameChange("Booking - Choice")
    }
  }, [onGameChange])

  React.useEffect(() => {
    if (onGameTimeChange) {
      onGameTimeChange(selectedGameTime)
    }
  }, [selectedGameTime, onGameTimeChange])

  // Set initial game time based on active session
  React.useEffect(() => {
    if (activeSession) {
      const sessionTime = `${activeSession.name} (${activeSession.drawTime})`
      setSelectedGameTime(sessionTime)
    }
  }, [activeSession])

  // Countdown timer
  React.useEffect(() => {
    const updateCountdown = () => {
      const now = new Date()
      let nextDraw: Date

      if (activeSession && activeSession.drawTime) {
        try {
          // Parse the drawTime string (format: "2025-12-02 17:03")
          const [datePart, timePart] = activeSession.drawTime.split(' ')
          const [year, month, day] = datePart.split('-').map(Number)
          const [hours, minutes] = timePart.split(':').map(Number)
          
          // Create draw time date
          nextDraw = new Date(year, month - 1, day, hours, minutes, 0, 0)
          
          // If draw time is in the past, use current date with the draw time
          if (nextDraw < now) {
            nextDraw.setDate(now.getDate())
            nextDraw.setMonth(now.getMonth())
            nextDraw.setFullYear(now.getFullYear())
            
            // If still in the past, move to next day
            if (nextDraw < now) {
              nextDraw.setDate(nextDraw.getDate() + 1)
            }
          }
        } catch (error) {
          console.error('Error parsing draw time:', error)
          // Fallback to current time + 1 hour
          nextDraw = new Date(now.getTime() + 60 * 60 * 1000)
        }
      } else {
        // Fallback to selected game time
        nextDraw = new Date(now)
        if (selectedGameTime.includes("Morning")) {
          nextDraw.setHours(13, 0, 0, 0)
        } else if (selectedGameTime.includes("Day")) {
          nextDraw.setHours(18, 0, 0, 0)
        } else {
          nextDraw.setHours(20, 0, 0, 0)
        }

        if (now > nextDraw) {
          nextDraw.setDate(nextDraw.getDate() + 1)
        }
      }

      const diff = nextDraw.getTime() - now.getTime()
      
      if (diff < 0) {
        setCountdown("00:00:00")
        return
      }

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setCountdown(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      )
    }

    // Initial call
    updateCountdown()

    const timer = setInterval(updateCountdown, 1000)
    return () => clearInterval(timer)
  }, [selectedGameTime, activeSession])

  // Session dropdown for reports
  const SessionDropdown = () => (
    <CustomDropdown
      trigger={
        <Button className="bg-white border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700 rounded-full px-4 py-2 text-sm flex items-center gap-1 flex-grow sm:flex-grow-0">
          {selectedSession ? 
            sessions.find(s => s.id === selectedSession)?.name || "Select Session" 
            : "All Sessions"} 
          <ChevronDown className="h-4 w-4" />
        </Button>
      }
    >
      <DropdownItem onClick={() => onSessionChange?.("")}>
        All Sessions
      </DropdownItem>
      {sessions.map((session) => (
        <DropdownItem key={session.id} onClick={() => onSessionChange?.(session.id)}>
          {session.name} ({session.drawTime}) - {session.active ? 'Active' : 'Closed'}
        </DropdownItem>
      ))}
    </CustomDropdown>
  )

  // Game time dropdown for booking
  const GameTimeDropdown = () => (
    <CustomDropdown
      trigger={
        <Button className="bg-white border border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700 rounded-full px-4 py-2 text-sm flex items-center gap-1 flex-grow sm:flex-grow-0">
          {activeSession ? 
            `${activeSession.name} (${activeSession.drawTime})` 
            : selectedGameTime} 
          <ChevronDown className="h-4 w-4" />
        </Button>
      }
    >
      {activeSession ? (
        <DropdownItem onClick={() => {}}>
          {activeSession.name} ({activeSession.drawTime}) - {activeSession.active ? '🟢 Active' : '🔴 Closed'}
        </DropdownItem>
      ) : (
        gameTimes.map((time) => (
          <DropdownItem key={time} onClick={() => setSelectedGameTime(time)}>
            {time}
          </DropdownItem>
        ))
      )}
    </CustomDropdown>
  )

  return (
    <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mb-4">
      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4">
        {/* Date Picker */}
        <Popover>
          <PopoverTrigger>
            <Button
              variant={"outline"}
              className={cn(
                "w-full sm:w-[180px] justify-start text-left font-normal bg-white border border-gray-300 rounded-md",
                !date && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "dd/MM/yyyy") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
          </PopoverContent>
        </Popover>

        {/* Game Time Dropdown */}
        {showSessionFilter ? (
          <SessionDropdown />
        ) : (
          <GameTimeDropdown />
        )}

        {/* Active Session Status */}
        {activeSession && (
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-full text-sm">
            <div className={cn(
              "w-2 h-2 rounded-full",
              activeSession.active ? "bg-green-500" : "bg-red-500"
            )} />
            <span className={cn(
              "font-semibold",
              activeSession.active ? "text-green-600" : "text-red-600"
            )}>
              {activeSession.active ? 'Active' : 'Closed'}
            </span>
          </div>
        )}

        {/* Countdown Timer */}
        <p className="text-red-600 font-semibold text-lg w-full text-center sm:w-auto sm:text-left sm:ml-auto mt-2 sm:mt-0">
          Countdown: {countdown}
        </p>
      </div>

      {/* Additional Session Info */}
      {activeSession && (
        <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-semibold">Session:</span> {activeSession.name}
            </div>
            <div>
              <span className="font-semibold">Draw Time:</span> {activeSession.drawTime}
            </div>
            <div>
              <span className="font-semibold">Status:</span> 
              <span className={cn(
                "ml-1",
                activeSession.active ? "text-green-600" : "text-red-600"
              )}>
                {activeSession.active ? 'Accepting Bets' : 'Draw Completed'}
              </span>
            </div>
            <div>
              <span className="font-semibold">Date:</span> {format(new Date(activeSession.date), "dd/MM/yyyy")}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}